import { useState, useEffect, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import notificationService from '../services/notificationService';
import { useUser } from '../contexts/UserContext';
import {
  filterActivitiesByRole,
  filterNotificationsByRole,
  getUserRole,
  isLoadNotification,
  isPaymentNotification
} from '../utils/roleAccess';

const NOTIFICATION_SERVICE_URL = process.env.REACT_APP_NOTIFICATION_SERVICE_URL || 'https://notif.ciertacorp.com';

// Helper function to get auth token (reactive - called when needed)
const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

export const useNotifications = () => {
  const { user } = useUser();
  const [notifications, setNotifications] = useState([]);
  const [activities, setActivities] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const socketRef = useRef(null);
  const [connectionStatus, setConnectionStatus] = useState('offline'); // 'connected' | 'reconnecting' | 'offline'
  const abortControllerRef = useRef(null);
  
  // Refs to prevent duplicate requests
  const isLoadingRef = useRef(false);
  const lastFetchRef = useRef(0);
  const initializedRef = useRef(false);

  const role = getUserRole(user);

  // Initialize Socket.IO connection
  useEffect(() => {
    const authToken = getAuthToken();
    if (!authToken) {
      setConnectionStatus('offline');
      return;
    }

    // Create socket connection - only send token, server determines user
    const socket = io(NOTIFICATION_SERVICE_URL, {
      auth: {
        token: authToken
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      timeout: 5000,
      // Suppress connection errors in console
      autoConnect: true
    });

    socketRef.current = socket;

    // Connection events
    socket.on('connect', () => {
      setConnectionStatus('connected');
      setError(null);
    });

    socket.on('disconnect', () => {
      setConnectionStatus('offline');
    });

    socket.on('reconnect_attempt', () => {
      setConnectionStatus('reconnecting');
    });

    socket.on('reconnect', () => {
      setConnectionStatus('connected');
    });

    socket.on('connect_error', () => {
      setConnectionStatus('offline');
    });

    // Listen for new notifications
    socket.on('notification', (notification) => {
      const visible = filterNotificationsByRole([notification], role);
      if (visible.length === 0) return;
      setNotifications(prev => {
        const exists = prev.some(n => n._id === notification._id);
        if (exists) return prev;
        return [notification, ...prev];
      });
      setUnreadCount(prev => prev + 1);
    });

    // Listen for unread notifications on connect
    socket.on('unread_notifications', (unreadNotifications) => {
      if (Array.isArray(unreadNotifications) && unreadNotifications.length > 0) {
        const visible = filterNotificationsByRole(unreadNotifications, role);
        if (visible.length === 0) return;
        setNotifications(prev => {
          const existingIds = new Set(prev.map(n => n._id));
          const newNotifications = visible.filter(n => !existingIds.has(n._id));
          return [...newNotifications, ...prev];
        });
        setUnreadCount(visible.length);
      }
    });

    // Listen for notification updates
    socket.on('notification:read', (data) => {
      setNotifications(prev =>
        prev.map(n =>
          n._id === data.notificationId ? { ...n, read: true } : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    });

    // Cleanup on unmount
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [role]); // Empty deps - token is fetched inside effect

  // Load notifications from API
  // GET /notifications/me?limit=50&skip=0
  const loadNotifications = useCallback(async (params = {}) => {
    const authToken = getAuthToken();
    if (!authToken) {
      return;
    }

    // Abort previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    // Prevent duplicate requests
    const now = Date.now();
    if (isLoadingRef.current || (now - lastFetchRef.current) < 1000) {
      return;
    }

    isLoadingRef.current = true;
    lastFetchRef.current = now;
    setLoading(true);
    setError(null);

    try {
      const response = await notificationService.getNotifications({ 
        limit: params.limit || 50,
        skip: params.skip || 0
      });
      
      const data = response?.data || response;
      
      // API returns { notifications, unreadCount }
      const notificationsList = data?.notifications || (Array.isArray(data) ? data : []);
      const apiUnreadCount = data?.unreadCount;

      // Sort notifications by date
      const sortedNotifications = notificationsList
        .sort((a, b) => new Date(b.createdAt || b.created_at) - new Date(a.createdAt || a.created_at));

      const visibleNotifications = filterNotificationsByRole(sortedNotifications, role);
      setNotifications(visibleNotifications);

      // For activities, use unread notifications (excluding loads and payments)
      const activityNotifications = visibleNotifications
        .filter(n => !n.read && !isLoadNotification(n) && !isPaymentNotification(n))
        .slice(0, 10);
      setActivities(filterActivitiesByRole(activityNotifications, role));

      // Update unread count from API or calculate locally
      if (typeof apiUnreadCount === 'number') {
        setUnreadCount(Math.min(apiUnreadCount, visibleNotifications.length));
      } else {
        const unread = visibleNotifications.filter(n => !n.read).length;
        setUnreadCount(unread);
      }
    } catch (err) {
      // Don't show error if aborted or service is not available
      if (err.name === 'AbortError') {
        return;
      }
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to load notifications:', err?.message);
      }
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [role]);

  // Load unread count
  const loadUnreadCount = useCallback(async () => {
    const authToken = getAuthToken();
    if (!authToken) {
      return;
    }

    try {
      const count = await notificationService.getUnreadCount();
      setUnreadCount(count);
    } catch (err) {
      // Silently fail - don't show error for unread count
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to load unread count:', err?.message);
      }
    }
  }, []);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    const authToken = getAuthToken();
    if (!authToken) return;
    
    try {
      await notificationService.markAsRead(notificationId);
      setNotifications(prev =>
        prev.map(n =>
          n._id === notificationId ? { ...n, read: true } : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to mark notification as read');
    }
  }, []);

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to mark all as read');
    }
  }, []);

  // Delete notification
  const deleteNotification = useCallback(async (notificationId) => {
    const authToken = getAuthToken();
    if (!authToken) return;
    
    try {
      // Find the notification before deleting to check if it was unread
      const deleted = notifications.find(n => n._id === notificationId);
      const wasUnread = deleted && !deleted.read;
      
      await notificationService.deleteNotification(notificationId);
      
      // Remove from notifications list
      setNotifications(prev => prev.filter(n => n._id !== notificationId));
      
      // Update unread count if deleted notification was unread
      if (wasUnread) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to delete notification');
    }
  }, [notifications]);

  // Stable refresh function
  const refresh = useCallback(() => {
    const authToken = getAuthToken();
    if (!authToken) return;
    loadNotifications({ limit: 50 });
    loadUnreadCount();
  }, [loadNotifications, loadUnreadCount]);

  // Initial load - only once
  useEffect(() => {
    const authToken = getAuthToken();
    if (initializedRef.current || !authToken) {
      return;
    }
    
    initializedRef.current = true;
    loadNotifications({ limit: 50 });
    loadUnreadCount();
  }, [loadNotifications, loadUnreadCount]);

  // Cleanup AbortController on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    notifications,
    activities,
    unreadCount,
    loading,
    error,
    connectionStatus, // 'connected' | 'reconnecting' | 'offline'
    loadNotifications,
    loadUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refresh
  };
};

